import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { AgentGroupService } from '@/services/database/AgentGroupService';

export const useAgentGroups = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["agent-groups", user?.id],
    queryFn: () => AgentGroupService.listGroups(user?.id || ""),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!user?.id,
  });
};

export const useAgentGroupCounts = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["agent-group-counts", user?.id],
    queryFn: () => AgentGroupService.getCountsByGroup(user?.id || ""),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!user?.id,
  });
};

export const useCreateAgentGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { name: string; description?: string }) => AgentGroupService.createGroup(params.name, params.description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-groups"] });
      toast.success("Group created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create group");
    },
  });
};

export const useUpdateAgentGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { groupId: number; name?: string; description?: string }) =>
      AgentGroupService.updateGroup(params.groupId, {
        name: params.name,
        description: params.description,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-groups"] });
      toast.success("Group updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update group");
    },
  });
};

export const useDeleteAgentGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: number) => AgentGroupService.deleteGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-group-counts"] });
      queryClient.invalidateQueries({ queryKey: ["agent-groups"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      toast.success("Group deleted and genies moved to Ungrouped");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete group");
    },
  });
};

export const useReorderAgentGroups = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupIds: number[]) => AgentGroupService.reorderGroups(groupIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-groups"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reorder groups");
    },
  });
};

export const useAssignAgentToGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { agentId: string; groupId: number | null }) =>
      AgentGroupService.assignAgentToGroup(params.agentId, params.groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-group-counts"] });
      queryClient.invalidateQueries({ queryKey: ["agent-groups"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      toast.success("Genie moved successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to move genie");
    },
  });
};
